import {
  Button,
  GlobeIcon,
  MailIcon,
  Skeleton,
  Typography,
} from "@/components/atoms";
import WebAppLayout from "@/components/organisms/WebAppLayout";
import { useWebDocumentTitle } from "@/core/hooks/useWebDocumentTitle";
import { useTranslation, useUIColors } from "@/core/hooks";
import { logger } from "@/core/logger";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { getResponsiveValue } from "@/core/utils/responsive";
import { Image } from "expo-image";
import { router } from "expo-router";
import React, { useState } from "react";
import {
  Linking,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";

interface VentureScreenWebProps {
  ventureId?: string | undefined;
  ventureName?: string | null | undefined;
  ventureLogo?: string | null | undefined;
  ventureDescription?: string | null | undefined;
  ventureImage?: string | null | undefined;
  servicesHeading?: string | null | undefined;
  services?:
    | {
        id: string;
        name: string;
        imageUrl: string | null;
        link?: string | null | undefined;
      }[]
    | undefined;
  websiteUrl?: string | null | undefined;
  contactEmail?: string | null | undefined;
  isLoading?: boolean | undefined;
}

export function VentureScreenWeb({
  ventureName,
  ventureLogo,
  ventureDescription,
  ventureImage,
  servicesHeading,
  services = [],
  websiteUrl,
  contactEmail,
  isLoading = false,
}: VentureScreenWebProps) {
  const colors = useUIColors();
  const { t } = useTranslation();
  const { isRTL } = useI18nContext();

  const handleBackPress = () => {
    router.back();
  };

  const handleWebsitePress = () => {
    if (websiteUrl) {
      Linking.openURL(websiteUrl).catch((error) =>
        logger.error("VentureScreen", "Website open failed", error),
      );
    } else {
      logger.warn("VentureScreen", "Website URL missing");
    }
  };

  const handleContactPress = () => {
    if (contactEmail) {
      Linking.openURL(`mailto:${contactEmail}`).catch((error) =>
        logger.error("VentureScreen", "Contact open failed", error),
      );
    } else {
      logger.warn("VentureScreen", "Contact email missing");
    }
  };

  const handleServicePress = (link?: string | null, serviceId?: string) => {
    if (link) {
      Linking.openURL(link).catch((error) =>
        logger.error("VentureScreen", "Service link open failed", error),
      );
      return;
    }
    logger.info("VentureScreen", "Service pressed:", serviceId);
  };

  const [heroLoading, setHeroLoading] = useState<boolean>(!!ventureImage);

  const showHeroSkeleton = isLoading || heroLoading;

  // Breadcrumbs for navigation
  const breadcrumbs = [
    { label: t("home.tabLabel"), path: "/(auth)/(home)" },
    { label: ventureName || "Venture" },
  ];

  useWebDocumentTitle(ventureName);

  return (
    <WebAppLayout
      activeTab="home"
      headerTitle={ventureName || "Venture Details"}
      breadcrumbs={breadcrumbs}
      showBackButton
      onBackPress={handleBackPress}
    >
      <View
        style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}
      >
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Two-column layout: Image on left, Content on right */}
          <View style={styles.contentLayout}>
            {/* Left Column - Venture Image */}
            <View style={styles.imageColumn}>
              {ventureImage ? (
                <>
                  <Image
                    source={{ uri: ventureImage }}
                    style={styles.ventureImage}
                    contentFit="cover"
                    cachePolicy="memory-disk"
                    transition={200}
                    onLoadStart={() => setHeroLoading(true)}
                    onLoadEnd={() => setHeroLoading(false)}
                    onLoad={() => setHeroLoading(false)}
                    onError={() => setHeroLoading(false)}
                  />
                  {showHeroSkeleton && (
                    <Skeleton
                      width="100%"
                      height="100%"
                      borderRadius={12}
                      style={styles.heroSkeleton}
                    />
                  )}
                </>
              ) : (
                showHeroSkeleton && (
                  <Skeleton width="100%" height={484} borderRadius={12} />
                )
              )}
            </View>

            {/* Right Column - Content */}
            <View style={styles.contentColumn}>
              <View
                style={{
                  paddingRight: isRTL ? 0 : 8,
                  paddingLeft: isRTL ? 8 : 0,
                  // width: "98%",
                  gap: 24,
                }}
              >
                {/* Venture Title above description */}
                <View style={styles.logoContainer}>
                  {isLoading ? (
                    <Skeleton width={180} height={24} borderRadius={4} />
                  ) : (
                    <Typography
                      variant="title1"
                      color="primary"
                      style={[
                        styles.ventureName,
                        {
                          textAlign: isRTL ? "right" : "left",
                          fontSize: 32,
                        },
                      ]}
                    >
                      {ventureName || "Venture"}
                    </Typography>
                  )}
                </View>

                {/* Venture Description */}
                <View style={styles.descriptionContainer}>
                  {isLoading ? (
                    <View style={{ gap: 8 }}>
                      <Skeleton width="100%" height={16} />
                      <Skeleton width="92%" height={16} />
                      <Skeleton width="85%" height={16} />
                    </View>
                  ) : (
                    <Typography
                      variant="body"
                      color="tertiary"
                      style={[
                        styles.description,
                        { textAlign: isRTL ? "right" : "left" },
                      ]}
                    >
                      {ventureDescription}
                    </Typography>
                  )}
                </View>

                {/* Action Buttons */}
                {isLoading ? (
                  <View style={styles.buttonsContainer}>
                    <Skeleton width="48%" height={44} borderRadius={8} />
                    <Skeleton width="48%" height={44} borderRadius={8} />
                  </View>
                ) : (
                  <View style={styles.buttonsContainer}>
                    <Button
                      title={t("ventures.website" as any)}
                      variant="outline"
                      onPress={handleWebsitePress}
                      style={[
                        styles.actionButton,
                        { backgroundColor: colors.backgroundLevel1 },
                      ]}
                      leading={<GlobeIcon size={18} />}
                      disabled={!websiteUrl}
                    />
                    <Button
                      title={t("ventures.contact" as any)}
                      variant="outline"
                      onPress={handleContactPress}
                      style={[
                        styles.actionButton,
                        { backgroundColor: colors.backgroundLevel1 },
                      ]}
                      leading={<MailIcon size={18} strokeWidth={2.5} />}
                      disabled={!contactEmail}
                    />
                  </View>
                )}
              </View>

              {/* Services grid */}
              {isLoading ? (
                <View style={styles.servicesSection}>
                  <Skeleton
                    width={180}
                    height={16}
                    style={{ marginBottom: 8 }}
                  />
                  <View style={styles.servicesGrid}>
                    {[...Array(2)].map((_, idx) => (
                      <Skeleton
                        key={idx}
                        width="48%"
                        height={160}
                        borderRadius={12}
                      />
                    ))}
                  </View>
                </View>
              ) : services.length > 0 ? (
                <View style={styles.servicesSection}>
                  <Typography
                    variant="headline"
                    color="primary"
                    style={[
                      styles.servicesTitle,
                      { textAlign: isRTL ? "right" : "left" },
                    ]}
                  >
                    {servicesHeading || "Our Services"}
                  </Typography>
                  <View style={styles.servicesGrid}>
                    {services.map((service) => (
                      <TouchableOpacity
                        key={service.id}
                        style={[
                          styles.serviceCard,
                          { backgroundColor: colors.backgroundLevel1 },
                        ]}
                        activeOpacity={0.85}
                        onPress={() =>
                          handleServicePress(service.link, service.id)
                        }
                      >
                        {service.imageUrl ? (
                          <Image
                            source={{ uri: service.imageUrl }}
                            style={styles.serviceImage}
                            contentFit="cover"
                            cachePolicy="memory-disk"
                            transition={200}
                          />
                        ) : (
                          <Skeleton
                            width="100%"
                            height={120}
                            borderRadius={8}
                          />
                        )}
                        <Typography
                          variant="subhead"
                          color="primary"
                          style={[
                            styles.serviceName,
                            { textAlign: isRTL ? "right" : "left" },
                          ]}
                          numberOfLines={2}
                        >
                          {service.name}
                        </Typography>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
              ) : null}
            </View>
          </View>
        </ScrollView>
      </View>
    </WebAppLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: getResponsiveValue(16, 32, 32),
    paddingVertical: 24,
    paddingBottom: 100,
  },
  logoContainer: {
    alignItems: "flex-start",
    paddingTop: 24,
    marginBottom: 12,
  },
  ventureName: {
    lineHeight: 40,
  },
  contentLayout: {
    flexDirection: "row",
    gap: 24,
  },
  imageColumn: {
    width: "52%",
    minHeight: 484,
  },
  ventureImage: {
    width: "100%",
    height: 484,
    borderRadius: 12,
  },
  heroSkeleton: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
  },
  contentColumn: {
    flex: 1,
    gap: 24,
    alignItems: "stretch",
  },
  descriptionContainer: {
    paddingHorizontal: 0,
  },
  description: {
    lineHeight: 22,
    fontSize: 16,
  },
  buttonsContainer: {
    flexDirection: "row",
    gap: 12,
    paddingHorizontal: 0,
  },
  actionButton: {
    flex: 1,
    borderWidth: 0,
  },
  servicesSection: {
    gap: 8,
  },
  servicesTitle: {
    marginBottom: 4,
  },
  servicesGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    columnGap: 10,
    rowGap: 12,
  },
  serviceCard: {
    width: "48.5%",
    borderRadius: 12,
    padding: 6,
    gap: 12,
    borderWidth: 0.5,
    borderColor: "#e5e5e5",
  },
  serviceImage: {
    width: "100%",
    aspectRatio: 120 / 80,
    borderRadius: 8,
  },
  serviceName: {
    paddingHorizontal: 4,
    paddingBottom: 4,
  },
});
